import { z } from 'zod';
import { uuid, isoDateTime } from './common.js';
import { addressSnapshot } from './orders.js';
/** docs/design/04-carrier-adapter.md. */
export const carrierId = z.enum(['gls', 'danske_fragtmaend', 'dachser']);
export const carrierMode = z.enum(['dry_run', 'test', 'live']);
/** Shipments that can still be cancelled. One in transit is cancelled only if the carrier agrees. */
export const CANCELLABLE_SHIPMENT_STATUSES = ['labelled', 'in_transit', 'failed', 'draft'];
export const CARRIER_NAMES = { gls: 'GLS', danske_fragtmaend: 'Danske Fragtmænd', dachser: 'Dachser' };
/** Services per carrier; GLS `auto` picks parcel shop, business or private from the order. */
export const CARRIER_SERVICES = {
    gls: [
        { code: 'auto', name: 'Automatisk (pakkeshop / erhverv / privat)' },
        { code: 'parcel_shop', name: 'Pakkeshop (ShopDelivery)' },
        { code: 'business', name: 'Erhverv' },
        { code: 'private', name: 'Privat (PrivateDelivery)' },
    ],
    danske_fragtmaend: [
        { code: 'PalleEnhedsForsendelse', name: 'Palleforsendelse' },
        { code: 'Stykgods', name: 'Stykgods' },
        { code: 'Erhversbilpakker', name: 'Erhvervsbilpakker' },
    ],
    dachser: [
        { code: 'targoflex', name: 'targo flex (europæisk gods)' },
        { code: 'targospeed', name: 'targo speed' },
    ],
};
/**
 * Credential and configuration fields per carrier, so the admin form is generic. Secret fields are
 * stored encrypted and returned masked.
 */
export const CARRIER_FIELDS = {
    gls: [
        { key: 'userName', label: 'Brugernavn (UserName)' },
        { key: 'password', label: 'Kode (Password)', secret: true },
        { key: 'customerId', label: 'Kundenummer (Customerid)' },
        { key: 'contactId', label: 'Kontakt-id (Contactid)' },
        { key: 'customsClientId', label: 'GLS Group API: client id (told uden for EU)', hint: 'App fra dev-portal.gls-group.net' },
        { key: 'customsClientSecret', label: 'GLS Group API: client secret', secret: true },
    ],
    danske_fragtmaend: [
        { key: 'userName', label: 'API-brugernavn', hint: 'Uden "Fragt\\" foran; det sætter systemet selv på' },
        { key: 'password', label: 'API-kode', secret: true },
        { key: 'clientId', label: 'Client ID (client_id)', hint: 'Tom = Danske Fragtmænds standard for tilstanden' },
        { key: 'clientSecret', label: 'Client-kode (client_secret)', secret: true, hint: 'Kun hvis Danske Fragtmænd har givet en kode til klienten' },
        { key: 'agreementNumber', label: 'Aftalenummer (AgreementNumber)' },
        { key: 'hubAgreement', label: 'Hub (HubAgreement)' },
        { key: 'goodsTypeFull', label: 'Godstype helpalle', hint: 'Fx PL1, fra aftalen' },
        { key: 'goodsTypeHalf', label: 'Godstype halvpalle', hint: 'Fx PL2' },
        { key: 'goodsTypeQuarter', label: 'Godstype kvartpalle', hint: 'Fx PL4' },
        { key: 'goodsTypeParcel', label: 'Godstype kolli', hint: 'Fx CLL' },
        { key: 'exchangePallets', label: 'Bytpaller', hint: 'ja eller nej' },
    ],
    dachser: [
        { key: 'apiKey', label: 'API-nøgle', secret: true },
        { key: 'customerNumber', label: 'Kundenummer (8 cifre)' },
        { key: 'forwarderId', label: 'Dachser-filial (3 cifre)', hint: 'Fra Dachsers kodeliste; tom = automatisk' },
        { key: 'division', label: 'Division', hint: 'T = industrigods, F = food' },
        { key: 'termCode', label: 'Leveringsbetingelse (term code)' },
        { key: 'packagingFull', label: 'Emballagekode helpalle', hint: 'Fx EU' },
        { key: 'packagingHalf', label: 'Emballagekode halvpalle', hint: 'Fx H1' },
        { key: 'packagingQuarter', label: 'Emballagekode kvartpalle', hint: 'Fx VP' },
    ],
};
export const carrierAccountDto = z.object({
    id: uuid,
    carrier: carrierId,
    name: z.string(),
    warehouseId: uuid.nullable(),
    warehouseCode: z.string().nullable(),
    mode: carrierMode,
    baseUrl: z.string().nullable(),
    /** Every field of CARRIER_FIELDS; secrets masked when set. */
    fields: z.record(z.string(), z.string()),
    active: z.boolean(),
    updatedAt: isoDateTime,
});
export const saveCarrierAccountRequest = z.object({
    carrier: carrierId,
    name: z.string().trim().min(1).max(100),
    warehouseId: uuid.nullable().optional(),
    mode: carrierMode.default('dry_run'),
    baseUrl: z.string().trim().max(300).nullable().optional(),
    fields: z.record(z.string(), z.string().max(500)).default({}),
    active: z.boolean().default(true),
});
export const destination = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^(DK|EU|WORLD|ANY|[A-Z]{2}(,[A-Z]{2})*)$/, 'DK, EU, WORLD, ANY or a comma list of country codes');
export const carrierRuleDto = z.object({
    id: uuid,
    priority: z.number().int(),
    packageKind: z.enum(['parcel', 'pallet']),
    destination: z.string(),
    shopId: uuid.nullable(),
    shopName: z.string().nullable(),
    warehouseId: uuid.nullable(),
    warehouseCode: z.string().nullable(),
    carrier: carrierId,
    service: z.string().nullable(),
    active: z.boolean(),
});
export const saveCarrierRuleRequest = z.object({
    priority: z.number().int().min(0).max(10000).default(100),
    packageKind: z.enum(['parcel', 'pallet']),
    destination: destination.default('ANY'),
    shopId: uuid.nullable().optional(),
    warehouseId: uuid.nullable().optional(),
    carrier: carrierId,
    service: z.string().trim().max(50).nullable().optional(),
    active: z.boolean().default(true),
});
export const shipmentStatus = z.enum(['draft', 'labelled', 'handed_over', 'in_transit', 'delivered', 'failed', 'cancelled']);
export const shipmentDto = z.object({
    id: uuid,
    number: z.string(),
    ownerType: z.enum(['fulfilment', 'batch', 'return']),
    ownerId: uuid,
    ownerNumber: z.string().nullable(),
    orderId: uuid.nullable(),
    orderNumber: z.string().nullable(),
    carrier: carrierId,
    service: z.string(),
    mode: carrierMode,
    status: shipmentStatus,
    receiver: addressSnapshot,
    packageCount: z.number().int(),
    weightG: z.number().int(),
    consignmentNumber: z.string().nullable(),
    trackingNumbers: z.array(z.string()),
    trackingUrl: z.string().nullable(),
    priceMinor: z.number().int().nullable(),
    customsDocumentId: uuid.nullable(),
    labelDocumentIds: z.array(uuid),
    documentIds: z.array(uuid),
    error: z.string().nullable(),
    bookedAt: isoDateTime.nullable(),
    bookedBy: z.string().nullable(),
    deliveredAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
});
export const shipmentEventDto = z.object({ at: isoDateTime, code: z.string().nullable(), description: z.string(), location: z.string().nullable() });
export const shipmentDetailDto = shipmentDto.extend({
    events: z.array(shipmentEventDto),
    log: z.array(z.object({ id: uuid, operation: z.string(), outcome: z.string(), error: z.string().nullable(), durationMs: z.number().int(), at: isoDateTime, request: z.unknown(), response: z.unknown() })),
});
/** What booking would do for a fulfilment or batch: one proposed shipment per carrier group. */
export const shippingGroupDto = z.object({
    key: z.string(),
    packageKind: z.enum(['parcel', 'pallet']),
    packageIds: z.array(uuid),
    packageCount: z.number().int(),
    weightG: z.number().int(),
    carrier: carrierId.nullable(),
    service: z.string().nullable(),
    accountId: uuid.nullable(),
    accountName: z.string().nullable(),
    mode: carrierMode.nullable(),
    /** For a multi-shipment batch: the fulfilment this group ships. */
    fulfilmentId: uuid.nullable(),
    fulfilmentNumber: z.string().nullable(),
    orderId: uuid.nullable(),
    parcelShopId: z.string().nullable(),
    receiver: addressSnapshot.nullable(),
    issues: z.array(z.string()),
});
export const shippingPlanDto = z.object({
    ownerType: z.enum(['fulfilment', 'batch']),
    ownerId: uuid,
    customsRequired: z.boolean(),
    groups: z.array(shippingGroupDto),
    shipments: z.array(shipmentDto),
});
export const shippingOwnerQuery = z.object({ ownerType: z.enum(['fulfilment', 'batch']), ownerId: uuid });
export const bookShipmentRequest = z.object({
    ownerType: z.enum(['fulfilment', 'batch']),
    ownerId: uuid,
    /** Override the routing for every group of this package kind. */
    overrides: z
        .array(z.object({ packageKind: z.enum(['parcel', 'pallet']), carrier: carrierId, service: z.string().max(50).nullable().optional() }))
        .default([]),
    labelPrinterId: uuid.nullable().optional(),
    documentPrinterId: uuid.nullable().optional(),
});
export const shipmentQuery = z.object({
    status: shipmentStatus.optional(),
    carrier: carrierId.optional(),
    orderId: uuid.optional(),
    ownerId: uuid.optional(),
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
});
export const reprintShipmentRequest = z.object({ labelPrinterId: uuid.nullable().optional(), documentPrinterId: uuid.nullable().optional() });
export const EU_COUNTRIES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];
/** Inside the EU customs union: no customs papers. Greenland and the Faroe Islands are outside. */
export const isEu = (cc) => !!cc && EU_COUNTRIES.includes(cc.toUpperCase());
/** Per-shop on-charging prices, minor units (docs/design/01 shop_shipping_rate). */
export const shopShippingRateDto = z.object({
    shopId: uuid,
    enabled: z.boolean(),
    packingFullMinor: z.number().int(),
    packingHalfMinor: z.number().int(),
    packingQuarterMinor: z.number().int(),
    packingColliMinor: z.number().int(),
    freightFullMinor: z.number().int(),
    freightHalfMinor: z.number().int(),
    freightQuarterMinor: z.number().int(),
    freightColliMinor: z.number().int(),
});
export const saveShopShippingRateRequest = shopShippingRateDto.omit({ shopId: true }).extend({
    packingFullMinor: z.number().int().min(0),
    packingHalfMinor: z.number().int().min(0),
    packingQuarterMinor: z.number().int().min(0),
    packingColliMinor: z.number().int().min(0),
    freightFullMinor: z.number().int().min(0),
    freightHalfMinor: z.number().int().min(0),
    freightQuarterMinor: z.number().int().min(0),
    freightColliMinor: z.number().int().min(0),
});
export const chargeQuery = z.object({
    shopId: uuid.optional(),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
/** Parcels summary: per shop and carrier over a date range. */
export const chargeSummaryRow = z.object({
    shopId: uuid,
    shopName: z.string(),
    carrier: carrierId,
    shipments: z.number().int(),
    palletFull: z.number().int(),
    palletHalf: z.number().int(),
    palletQuarter: z.number().int(),
    colli: z.number().int(),
    packingMinor: z.number().int(),
    freightMinor: z.number().int(),
    shopPaidMinor: z.number().int(),
});
export const parcelShopDto = z.object({
    number: z.string(),
    name: z.string(),
    street: z.string(),
    zip: z.string(),
    city: z.string(),
    countryCode: z.string(),
    distanceM: z.number().int().nullable(),
});
export const parcelShopQuery = z.object({
    zip: z.string().trim().min(3).max(10),
    street: z.string().trim().max(100).optional(),
    country: z.string().trim().length(2).default('DK'),
});
export const setParcelShopRequest = z.object({ parcelShopId: z.string().trim().regex(/^\d{3,8}$/) });
export const endOfDayRequest = z.object({
    carrier: carrierId,
    warehouseId: uuid.optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    printerId: uuid.nullable().optional(),
});
//# sourceMappingURL=shipping.js.map